<template>
  <div class="monitoring">
    <div class="monitoring__danger" @click="openMenuAlerts">
      <i class="icon-nav-information" />
      <span>{{ alertText }}</span>
      <i class="icon-nav-bullet_down" />
    </div>

    <ul
      class="monitoring__alerts-list"
      v-if="showAlerts"
      @mouseleave="mouseLeaveMenuAlert"
    >
      <li class="monitoring__danger">
        <i class="icon-nav-information" />
        <span>{{ alertText }}</span>
        <i class="icon-nav-bullet_up" />
      </li>

      <template v-for="(alert, index) in alerts" :key="index">
        <li @click="showDialog(alert)" :id="alert.cod">
          <span>{{ alert.title }}</span>
          <i class="icon-nav-arrow_right" />
        </li>
      </template>
    </ul>
  </div>

  <Dialog
    class="dialog-alert"
    :visible="isVisible"
    :draggable="false"
    :modal="true"
    :closable="true"
    @update:visible="hideDialog"
  >
    <template #header>
      <div class="dialog-alert__header">
        <span class="dialog-alert__title">{{ alert.title }}</span>
      </div>
    </template>
    <div class="dialog-alert__content">
      <p class="dialog-alert__location">Seção onde ocorre:</p>
      <p class="dialog-alert__section">{{ props.title }}</p>

      <div class="dialog-alert__box">
        <p class="dialog-alert__subtitle" v-html="alert.subtitle"></p>
        <p class="dialog-alert__problem">
          {{ alert.cod }}
        </p>
        <p class="dialog-alert__recomendation">{{ alert.recommendation }}</p>
      </div>
    </div>
    <template #footer>
      <div class="dialog-alert__footer">
        <i class="icon-nav-information" />
        <p>
          Siga a recomendação para melhorar a saúde da sua base. Se o problema
          persistir fale com seu account.
        </p>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";

export default defineComponent({
  name: "SelectMonitoringAlert",

  props: {
    alerts: { type: Array, required: true },
    title: { type: String, required: true },
  },

  setup(props) {
    //DATA
    const showAlerts = ref(false);
    let isVisible = ref(false);
    let alert = ref({});

    const alertText = computed(() => {
      let count = props.alerts.length;
      return count === 1 ? "1 ponto de atenção" : `${count} pontos de atenção`;
    });

    // METHODS
    const openMenuAlerts = () => {
      window.removeEventListener("click", openMenuAlerts, false);

      showAlerts.value = !showAlerts.value;
    };

    const mouseLeaveMenuAlert = () => {
      window.addEventListener("click", openMenuAlerts, false);
    };

    const showDialog = (param: any) => {
      isVisible.value = !isVisible.value;

      alert.value = param;
    };

    const hideDialog = () => {
      isVisible.value = false;
    };

    // WATCH
    watch(isVisible, (newVal: boolean) => {
      isVisible.value = newVal;
    });

    return {
      showAlerts,
      openMenuAlerts,
      mouseLeaveMenuAlert,
      props,
      alertText,
      showDialog,
      isVisible,
      alert,
      hideDialog,
    };
  },
});
</script>

<style lang="scss">
.monitoring {
  position: relative;
  display: inline-block;

  &__danger {
    cursor: pointer;
    color: var(--insights-color-27);
    font-family: "text-medium";
    font-size: toRem(17px);
    line-height: toRem(20px);
    display: flex;
    align-items: center;

    &:hover {
      color: var(--insights-color-27) !important;
    }

    > i.icon-nav-bullet_down,
    i.icon-nav-bullet_up {
      margin-left: 10px;
      font-size: toRem(11px);
    }

    > i.icon-nav-information {
      margin-right: 10px;
    }
  }

  &__alerts-list {
    list-style: none;
    position: absolute;
    top: 0;
    right: 0;
    background: var(--insights-color-2);
    width: 485px;
    padding: 1.1rem 1.3rem;
    box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    color: #989898;
    font-size: toRem(15px);
    z-index: 999;

    li {
      cursor: pointer;
      margin-bottom: toRem(13px);
      padding-bottom: toRem(12px);
      color: #838383;
      font-family: "text-medium";
      font-size: toRem(17px);
      line-height: toRem(20px);
      border-bottom: 1px dashed #c9c9c9;

      &.monitoring__danger {
        > i.icon-nav-bullet_up {
          color: var(--insights-color-16);
          margin-left: auto;
        }
      }

      &:hover {
        color: var(--insights-color-1);
      }

      &:first-child {
        color: var(--insights-color-27);
        cursor: default;
        margin-bottom: 20px;
        border: none;
      }

      &:last-child {
        margin-bottom: 0px;
        border: none;
      }

      > i.icon-nav-arrow_right {
        color: var(--insights-color-24);
        margin-left: 10px;
      }
    }
  }
}

.dialog-alert {
  width: 30vw;
  padding: 15px;
  background: var(--insights-color-2);

  &__title {
    color: var(--insights-color-27);
    font-family: "text-regular";
    font-size: toRem(30px);
    line-height: toRem(35px);
  }

  &__location {
    color: #838383;
    font-family: "text-medium";
    font-size: toRem(17px);
    line-height: toRem(20px);
    margin-bottom: 0;
  }

  &__section {
    color: var(--insights-color-1);
    font-family: "text-medium";
    font-size: toRem(22px);
    line-height: toRem(26px);
    margin: 5px 0 30px;
  }

  &__box {
    padding: 30px;
    background: #fbfbfb;
    border-radius: 4px;
    box-shadow: inset 0px 0px 6px rgba(0, 0, 0, 0.15);
  }

  &__subtitle {
    color: var(--insights-color-27);
    font-family: "text-semi-bold";
    font-size: toRem(18px);
    line-height: toRem(22px);

    b {
      color: var(--insights-color-1);
    }
  }

  &__problem {
    color: #616161;
    font-family: "text-semi-bold";
    font-size: toRem(18px);
    line-height: toRem(21px);
    margin-bottom: 5px;
  }

  &__recomendation {
    color: #616161;
    font-family: "text-medium";
    font-size: toRem(17px);
    line-height: toRem(20px);
    margin-bottom: 0px;
  }

  &__footer {
    display: flex;

    p {
      color: var(--insights-color-16);
      font-family: "text-medium";
      font-size: toRem(15px);
      line-height: toRem(18px);
      text-align: left;
      margin-bottom: 0;
    }

    i {
      color: var(--insights-color-16);
      margin-right: 10px;
    }
  }
}

@media screen and (max-width: 1500px) {
  .dialog-alert {
    width: 50vw;
  }
}

@media screen and (min-width: 1501px) and (max-width: 1800px) {
  .dialog-alert {
    width: 40vw;
  }
}

.p-dialog .p-dialog-header .p-dialog-header-icon {
  color: #cdcbcb;
  top: 15px;
  right: 15px;
  position: absolute;
}
</style>
